import { RIYAL } from "@/lib/currency";
import { useState, useEffect, useMemo, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Upload, Loader2, CreditCard } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { contractsRepository } from "@/repositories/contracts";
import { propertiesRepository } from "@/repositories/properties";
import { receiptsRepository } from "@/repositories/receipts";
import { invoicesRepository } from "@/repositories/invoices";
import { unitsRepository } from "@/repositories/units";
import type { PaymentChannel, TransactionType } from "@/types/database";
import { toast } from "sonner";
import { formatNumber } from "@/lib/formatters";
import { defaultPaymentTypeForInvoice } from "@/lib/financeHelpers";

interface AddPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Optional pre-selection props for contextual use
  preSelectedPropertyId?: string;
  preSelectedUnitId?: string;
  preSelectedContractId?: string;
  preSelectedInvoiceId?: string;
  onSuccess?: () => void;
}

// Check if we're in pre-filled context mode (invoice was provided)
const usePreFilledContext = (invoiceId?: string) => !!invoiceId;

const PAYMENT_TYPES = [
  { value: "rent", label: "إيجار", labelEn: "Rent" },
  { value: "service_fee", label: "رسوم خدمات", labelEn: "Service Fee" },
  { value: "maintenance", label: "صيانة", labelEn: "Maintenance" },
  { value: "insurance", label: "تأمين", labelEn: "Insurance" },
  { value: "deposit", label: "تأمين مسترد", labelEn: "Deposit" },
  { value: "other", label: "أخرى", labelEn: "Other" },
];

// Card / MADA / online payments are recorded automatically by the HyperPay
// callback flow (hyperpay-invoice-status). Listing them here would let an
// admin double-record a payment that the gateway already booked. Leave only
// the offline channels that actually need manual entry.
const PAYMENT_CHANNELS = [
  { value: "bank_transfer", label: "تحويل بنكي", labelEn: "Bank Transfer" },
  { value: "cash", label: "نقدي", labelEn: "Cash" },
  { value: "ejar_network", label: "شبكة إيجار", labelEn: "Ejar Network" },
  { value: "check", label: "شيك", labelEn: "Check" },
];

export function AddPaymentDialog({
  open,
  onOpenChange,
  preSelectedPropertyId,
  preSelectedUnitId,
  preSelectedContractId,
  preSelectedInvoiceId,
  onSuccess,
}: AddPaymentDialogProps) {
  const { t, language } = useLanguage();
  const { companyId } = useAuth();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);
  // Stable idempotency key for the current payment intent. Generated on the
  // first submit attempt and reused across retries/double-clicks of the SAME
  // payment so the guarded POST /receipts endpoint dedupes them into ONE receipt
  // (Fortress rule 2 — a lost-response retry must never double-record). Cleared
  // on success so the next distinct payment gets a fresh key.
  const idempotencyKeyRef = useRef<string | null>(null);
  const [propertyId, setPropertyId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [contractId, setContractId] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [date, setDate] = useState<Date>(new Date());
  const [amount, setAmount] = useState("");
  const [paymentChannel, setPaymentChannel] = useState("");
  const [notes, setNotes] = useState("");
  const [document, setDocument] = useState<File | null>(null);

  // Check if we're in context mode (pre-filled from invoice)
  const isContextMode = usePreFilledContext(preSelectedInvoiceId);

  // Fetch invoice details when preSelectedInvoiceId is provided
  const { data: preFilledInvoice, isLoading: isLoadingPreFill } = useQuery({
    queryKey: ["invoice-prefill", preSelectedInvoiceId],
    queryFn: async () => {
      if (!preSelectedInvoiceId) return null;
      return invoicesRepository.getPreFill(preSelectedInvoiceId, companyId ?? "");
    },
    enabled: open && !!preSelectedInvoiceId && !!companyId,
  });

  // Reset form when dialog opens - handle both context and manual modes
  useEffect(() => {
    if (open) {
      if (isContextMode && preFilledInvoice) {
        // Context mode: pre-fill from invoice data
        setPropertyId(preFilledInvoice.property_id || "");
        setUnitId(preFilledInvoice.unit_id || "");
        setContractId(preFilledInvoice.contract_id || "");
        setInvoiceId(preFilledInvoice.id);
        // Derive the default from the invoice itself — an expense-generated
        // invoice (maintenance, insurance, …) must not default to "rent"
        // (ops ticket: maintenance payment showed Rent, locked). The operator
        // can still override it — the select stays enabled in context mode.
        setPaymentType(
          defaultPaymentTypeForInvoice({
            sourceExpenseType: preFilledInvoice.source_expense_type,
            contractType: preFilledInvoice.contract?.contract_type,
            description: preFilledInvoice.description,
          }),
        );
        setDate(new Date()); // Today's date for actual payment
        setAmount(String(preFilledInvoice.balance || preFilledInvoice.total_amount));
      } else if (!isContextMode) {
        // Manual mode: use pre-selected values or empty
        setPropertyId(preSelectedPropertyId || "");
        setUnitId(preSelectedUnitId || "");
        setContractId(preSelectedContractId || "");
        setInvoiceId("");
        setPaymentType("");
        setDate(new Date());
        setAmount("");
      }
      // Always reset these fields
      setPaymentChannel("");
      setNotes("");
      setDocument(null);
    }
  }, [open, preSelectedPropertyId, preSelectedUnitId, preSelectedContractId, isContextMode, preFilledInvoice]);

  // Fetch properties
  const { data: properties = [] } = useQuery({
    queryKey: ["properties", companyId],
    queryFn: async () => {
      if (!companyId) return [];
      return propertiesRepository.listForPaymentDialog(companyId);
    },
    enabled: open && !!companyId,
  });

  // Fetch units filtered by selected property
  const { data: units = [] } = useQuery({
    queryKey: ["units", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      return unitsRepository.listForPaymentDialog(propertyId);
    },
    enabled: open && !!propertyId,
  });

  // Fetch active contracts filtered by selected unit
  const { data: contracts = [] } = useQuery({
    queryKey: ["contracts", unitId, "active"],
    queryFn: async () => {
      if (!unitId || !companyId) return [];
      return contractsRepository.listActiveByUnit(unitId, companyId);
    },
    enabled: open && !!unitId && !!companyId,
  });

  // Fetch unpaid invoices for selected contract
  const { data: invoices = [] } = useQuery({
    queryKey: ["invoices", contractId, "unpaid"],
    queryFn: async () => {
      if (!contractId || !companyId) return [];
      return invoicesRepository.listUnpaidByContract(contractId, companyId);
    },
    enabled: open && !!contractId && !!companyId,
  });

  // Find the selected invoice details (for manual mode)
  const selectedInvoice = useMemo(() => {
    if (isContextMode && preFilledInvoice) {
      return {
        id: preFilledInvoice.id,
        invoice_number: preFilledInvoice.invoice_number,
        due_date: preFilledInvoice.due_date,
        total_amount: preFilledInvoice.total_amount,
        balance: preFilledInvoice.balance,
        status: preFilledInvoice.status,
      };
    }
    return invoices.find((inv) => inv.id === invoiceId);
  }, [invoices, invoiceId, isContextMode, preFilledInvoice]);

  // Auto-fill amount when invoice is selected (manual mode only)
  useEffect(() => {
    if (!isContextMode && selectedInvoice) {
      setAmount(String(selectedInvoice.balance || selectedInvoice.total_amount));
    }
  }, [selectedInvoice, isContextMode]);

  // Reset cascading selects when parent changes (manual mode only)
  useEffect(() => {
    if (!isContextMode && !preSelectedUnitId) {
      setUnitId("");
      setContractId("");
      setInvoiceId("");
    }
  }, [propertyId, preSelectedUnitId, isContextMode]);

  useEffect(() => {
    if (!isContextMode && !preSelectedContractId) {
      setContractId("");
      setInvoiceId("");
    }
  }, [unitId, preSelectedContractId, isContextMode]);

  useEffect(() => {
    if (!isContextMode && !preSelectedInvoiceId) {
      setInvoiceId("");
    }
  }, [contractId, preSelectedInvoiceId, isContextMode]);

  const getPropertyLabel = (prop: { name: string; name_en?: string | null; deed_number?: string | null }) => {
    const name = language === "ar" ? prop.name : prop.name_en || prop.name;
    return prop.deed_number ? `${name} (${prop.deed_number})` : name;
  };

  const getContractLabel = (contract: { contract_number?: string | null; tenant?: { first_name?: string; last_name?: string; commercial_name?: string } | null }) => {
    const tenantName = contract.tenant?.commercial_name || `${contract.tenant?.first_name || ""} ${contract.tenant?.last_name || ""}`.trim();
    return `${contract.contract_number || "-"} - ${tenantName || t("بدون مستأجر", "No Tenant")}`;
  };

  const getInvoiceLabel = (inv: { invoice_number?: string | null; due_date: string; balance: number }) => {
    const dueDate = format(new Date(inv.due_date), "yyyy/MM/dd");
    return `${inv.invoice_number || "-"} - ${dueDate} - ${formatNumber(inv.balance)}\u00A0${RIYAL}`;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(t("حجم الملف يجب أن يكون أقل من 2 ميجابايت", "File size must be less than 2MB"));
        return;
      }
      setDocument(file);
    }
  };

  const toPaymentChannel = (channel: string) => {
    const mapping: Record<string, string> = {
      bank_transfer: "bank_transfer",
      cash: "cash",
      credit_card: "card",
      mada: "card",
      ejar_network: "other",
      online: "other",
      check: "check",
    };
    return mapping[channel] || "other";
  };

  const handleSubmit = async () => {
    if (!companyId) {
      toast.error(t("خطأ في الجلسة", "Session error"));
      return;
    }
    // Only the invoice and payment type are hard requirements —
    // expense-generated invoices (owner/company responsibility) legitimately
    // have NO contract/unit, and the record_payment RPC treats
    // contract/unit/property/tenant as optional. In manual mode the invoice
    // can only be picked through the property→unit→contract cascade, so those
    // stay implicitly required there. The explicit type check stops a manual
    // submit with an unset type from silently recording as "rent".
    if (!invoiceId || !paymentType) {
      toast.error(t("يرجى تعبئة جميع الحقول المطلوبة", "Please fill all required fields"));
      return;
    }
    // Payment Channel is marked required in the form (Channel * label) but
    // wasn't validated before submit — empty channel mapped silently to
    // "other", losing the operator's intent on the receipt + reconciliation.
    // Block submission until a channel is picked.
    if (!paymentChannel) {
      toast.error(t("يرجى اختيار قناة الدفع", "Please select a payment channel"));
      return;
    }

    const paymentAmount = Number(amount);
    if (paymentAmount <= 0) {
      toast.error(t("المبلغ يجب أن يكون أكبر من صفر", "Amount must be greater than zero"));
      return;
    }

    // Mint a stable idempotency key for THIS payment intent on the first attempt;
    // reuse it on any retry/double-click so the endpoint records exactly one receipt.
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }

    setIsLoading(true);
    try {
      // Fetch invoice metadata (include scheduled_payment_id for sync)
      const invoiceMeta = await invoicesRepository.getMeta(invoiceId, companyId);
      if (!invoiceMeta) throw new Error("Invoice not found");

      // Upload document if provided. #3059 C1 phase 2: receipt proofs are PII
      // and now go to the private `documents` bucket, company-scoped path —
      // `documents` has no public URL, so only the bare PATH is persisted
      // (receipts.document_url); resolve a fresh signed URL at read time via
      // resolveDocumentUrl (src/lib/storage.ts).
      let documentUrl: string | null = null;
      if (document) {
        const ext = document.name.split(".").pop();
        const filePath = `${companyId}/receipts/${invoiceId}_${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("documents")
          .upload(filePath, document);

        if (uploadError) throw uploadError;

        documentUrl = filePath;
      }

      // Map UI channel/type to the validated DB enums
      const validPaymentChannels = ["bank_transfer", "card", "cash", "check", "ejar", "other", "sadad"] as const;
      const validTransactionTypes = ["deposit", "maintenance", "other", "penalty", "refund", "rent", "service"] as const;

      const mappedChannel = toPaymentChannel(paymentChannel);
      const finalChannel = (validPaymentChannels.includes(mappedChannel as never) ? mappedChannel : "other") as PaymentChannel;

      // paymentType is guaranteed non-empty here — both the Confirm disabled
      // guard and the early return above require it.
      const mappedType = paymentType;
      const typeMapping: Record<string, typeof validTransactionTypes[number]> = {
        rent: "rent",
        service_fee: "service",
        maintenance: "maintenance",
        insurance: "deposit",
        deposit: "deposit",
        other: "other",
      };
      const finalTransactionType = (typeMapping[mappedType] || "other") as TransactionType;

      // Record the payment via the receipts repository → record_payment RPC:
      // atomic receipt insert (with generated receipt_number) + invoice balance
      // reconciliation (incl. overpaid). Replaces the prior direct insert +
      // manual invoice update, which bypassed double-entry.
      const created = await receiptsRepository.create(
        companyId,
        {
          amount: paymentAmount,
          payment_channel: finalChannel,
          transaction_type: finalTransactionType,
          payment_date: format(date, "yyyy-MM-dd"),
          invoice_id: invoiceId,
          // Optional on expense-generated invoices (no contract/unit) — the
          // record_payment RPC accepts them as NULL.
          contract_id: contractId || undefined,
          tenant_id: invoiceMeta.tenant_id ?? undefined,
          property_id: propertyId || undefined,
          unit_id: unitId || undefined,
          notes: notes || undefined,
        },
        idempotencyKeyRef.current ?? undefined,
      );

      // Attach the uploaded payment proof (record_payment doesn't carry it).
      if (documentUrl) {
        const receiptId = (created as { id?: string } | null)?.id;
        if (receiptId) {
          await receiptsRepository.attachDocument(receiptId, companyId, documentUrl);
        }
      }

      // record_payment already reconciled the invoice; compute the post-state
      // only to sync the linked scheduled_payment.
      const invoice = selectedInvoice!;
      const newPaidAmount = (invoice.total_amount - invoice.balance) + paymentAmount;
      const newBalance = invoice.balance - paymentAmount;

      // Sync linked scheduled_payment status
      if (invoiceMeta.scheduled_payment_id) {
        const spStatus = newBalance <= 0 ? "paid" : "partial";
        await invoicesRepository.syncScheduledPayment({
          scheduledPaymentId: invoiceMeta.scheduled_payment_id,
          status: spStatus,
          paidAmount: newPaidAmount,
        });
      }

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ["receipts"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      // The detail views key on ["invoice", id] (singular — NOT a prefix of
      // ["invoices"]); without this the InvoiceDetails page keeps the stale
      // balance after recording a payment from it.
      queryClient.invalidateQueries({ queryKey: ["invoice"] });
      queryClient.invalidateQueries({ queryKey: ["finance-summary-stats"] });
      queryClient.invalidateQueries({ queryKey: ["finance-totals-summary"] });
      queryClient.invalidateQueries({ queryKey: ["scheduled_payments"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-payments"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-receipts"] });
      queryClient.invalidateQueries({ queryKey: ["owner-financials"] });
      queryClient.invalidateQueries({ queryKey: ["owner-documents"] });

      // Payment committed — retire this intent's idempotency key so the next
      // distinct payment mints a fresh one.
      idempotencyKeyRef.current = null;

      toast.success(t("تم تسجيل الدفعة بنجاح", "Payment recorded successfully"));
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Error recording payment:", error);
      toast.error(t("حدث خطأ أثناء تسجيل الدفعة", "Error recording payment"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            {t("تسجيل دفعة", "Record Payment")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("سجّل دفعة يدوية عن طريق اختيار العقار والوحدة والعقد والفاتورة وقناة الدفع والمبلغ", "Record a manual payment by selecting the property, unit, contract, invoice, payment channel, and amount")}
          </DialogDescription>
        </DialogHeader>

        {/* Loading state for context mode pre-filling */}
        {isContextMode && isLoadingPreFill ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Property */}
            <div className="space-y-2">
              <Label>{t("العقار", "Property")} *</Label>
              <Select
                value={propertyId}
                onValueChange={setPropertyId}
                disabled={isContextMode || !!preSelectedPropertyId}
              >
                <SelectTrigger className={isContextMode ? "bg-muted" : ""}>
                  <SelectValue placeholder={t("اختر العقار", "Select property")} />
                </SelectTrigger>
                <SelectContent>
                  {isContextMode && preFilledInvoice?.property ? (
                    <SelectItem value={preFilledInvoice.property.id}>
                      {getPropertyLabel(preFilledInvoice.property)}
                    </SelectItem>
                  ) : (
                    properties.map((prop) => (
                      <SelectItem key={prop.id} value={prop.id}>
                        {getPropertyLabel(prop)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Unit */}
            <div className="space-y-2">
              <Label>{t("الوحدة", "Unit")} *</Label>
              <Select
                value={unitId}
                onValueChange={setUnitId}
                disabled={isContextMode || !propertyId || !!preSelectedUnitId}
              >
                <SelectTrigger className={isContextMode ? "bg-muted" : ""}>
                  <SelectValue placeholder={t("اختر الوحدة", "Select unit")} />
                </SelectTrigger>
                <SelectContent>
                  {isContextMode && preFilledInvoice?.unit ? (
                    <SelectItem value={preFilledInvoice.unit.id}>
                      {preFilledInvoice.unit.unit_number}
                    </SelectItem>
                  ) : (
                    units.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.unit_number}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Contract */}
            <div className="space-y-2">
              <Label>{t("العقد", "Contract")} *</Label>
              <Select
                value={contractId}
                onValueChange={setContractId}
                disabled={isContextMode || !unitId || !!preSelectedContractId}
              >
                <SelectTrigger className={isContextMode ? "bg-muted" : ""}>
                  <SelectValue placeholder={t("اختر العقد", "Select contract")} />
                </SelectTrigger>
                <SelectContent>
                  {isContextMode && preFilledInvoice?.contract ? (
                    <SelectItem value={preFilledInvoice.contract.id}>
                      {getContractLabel(preFilledInvoice.contract)}
                    </SelectItem>
                  ) : (
                    contracts.map((contract) => (
                      <SelectItem key={contract.id} value={contract.id}>
                        {getContractLabel(contract)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Payment Type — stays editable in context mode: the invoice
                only provides a smart DEFAULT (see defaultPaymentTypeForInvoice);
                the operator must be able to correct it. */}
            <div className="space-y-2">
              <Label>{t("نوع الدفعة", "Payment Type")} *</Label>
              <Select
                value={paymentType}
                onValueChange={setPaymentType}
              >
                <SelectTrigger data-testid="payment-type-trigger">
                  <SelectValue placeholder={t("اختر نوع الدفعة", "Select payment type")} />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {t(type.label, type.labelEn)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Scheduled Payment (Invoice) */}
            <div className="space-y-2">
              <Label>{t("الدفعة المجدولة", "Scheduled Payment")} *</Label>
              <Select
                value={invoiceId}
                onValueChange={setInvoiceId}
                disabled={isContextMode || !contractId || !!preSelectedInvoiceId}
              >
                <SelectTrigger className={isContextMode ? "bg-muted" : ""}>
                  <SelectValue placeholder={t("اختر الدفعة المجدولة", "Select scheduled payment")} />
                </SelectTrigger>
                <SelectContent>
                  {isContextMode && preFilledInvoice ? (
                    <SelectItem value={preFilledInvoice.id}>
                      {getInvoiceLabel({
                        invoice_number: preFilledInvoice.invoice_number,
                        due_date: preFilledInvoice.due_date,
                        balance: preFilledInvoice.balance,
                      })}
                    </SelectItem>
                  ) : (
                    invoices.map((inv) => (
                      <SelectItem key={inv.id} value={inv.id}>
                        {getInvoiceLabel(inv)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label>{t("التاريخ", "Date")} *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-start font-normal",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="me-2 h-4 w-4" />
                    {date ? format(date, "dd/MM/yyyy") : t("اختر التاريخ", "DD/MM/YYYY")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label>{t("المبلغ", "Amount")} *</Label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder={t("أدخل المبلغ", "Enter amount")}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pe-12"
                />
                <span className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  {RIYAL}
                </span>
              </div>
            </div>

            {/* Payment Channel */}
            <div className="space-y-2">
              <Label>{t("قناة الدفع", "Payment Channel")} *</Label>
              <Select value={paymentChannel} onValueChange={setPaymentChannel}>
                <SelectTrigger>
                  <SelectValue placeholder={t("اختر قناة الدفع", "Select payment channel")} />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_CHANNELS.map((channel) => (
                    <SelectItem key={channel.value} value={channel.value}>
                      {t(channel.label, channel.labelEn)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>{t("ملاحظات", "Notes")}</Label>
            <Textarea
              placeholder={t("أدخل الملاحظات", "Enter notes")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {/* Document Upload */}
          <div className="space-y-2">
            <Label>{t("المستند", "Document")}</Label>
            <label className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer block">
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              {document ? (
                <p className="text-sm font-medium text-primary">{document.name}</p>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    {t("اسحب وأفلت هنا، أو تصفح", "Drag & drop here, or browse")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PNG, JPG, GIF or PDF (max 2MB)
                  </p>
                </>
              )}
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("إلغاء", "Cancel")}
          </Button>
          <Button
            data-testid="record-receipt-submit-btn"
            onClick={handleSubmit}
            disabled={isLoading || !paymentChannel || !paymentType || !invoiceId || !amount || Number(amount) <= 0}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin me-2" />
                {t("جاري الحفظ...", "Saving...")}
              </>
            ) : (
              t("تأكيد", "Confirm")
            )}
          </Button>
        </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
